import moment from 'moment'

export class ConflictManager {
  constructor (controller) {
    this.controller = controller
    this.overlapConflict = null
  }

  /**
     * Check for overlapping intervals
     * @param {Date} startTime - Start time to check
     * @param {Date} endTime - End time to check
     * @param {number} excludeIntervalId - Exclude this interval from check
     * @returns {Promise<Array>} Promise resolving to array of overlapping events
     */
  checkForOverlaps (startTime, endTime, excludeIntervalId) {
    const overlappingEvents = this.findOverlappingEvents(startTime, endTime, excludeIntervalId)
    return Promise.resolve(overlappingEvents)
  }

  /**
     * Find all events that overlap with the given time range or event
     * @param {Object|Date} target - Either an event object or start time
     * @param {Date} endTime - End time (if target is start time)
     * @param {number} excludeIntervalId - Optional interval ID to exclude from results
     * @returns {Array} Array of overlapping events
     */
  findOverlappingEvents (target, endTime = null, excludeIntervalId = null) {
    if (!this.controller.calendarManager.calendar) return []

    const allEvents = this.controller.calendarManager.calendar.getEvents()
    const overlappingEvents = []

    let targetStart, targetEnd, targetId

    if (endTime) {
      // Called with (startTime, endTime, excludeId) - for new intervals
      targetStart = moment(target)
      targetEnd = moment(endTime)
      targetId = excludeIntervalId
    } else {
      // Called with (event) - for existing events
      targetStart = moment(target.start)
      targetEnd = moment(target.end)
      targetId = target.extendedProps?.intervalId || excludeIntervalId
    }

    allEvents.forEach(event => {
      const eventProps = event.extendedProps
      if (!eventProps || !eventProps.intervalId || eventProps.intervalId === targetId) {
        return // Skip non-interval events or excluded event
      }

      const eventStart = moment(event.start)
      const eventEnd = moment(event.end)

      // Check if intervals overlap using standard overlap detection
      if (targetStart.isBefore(eventEnd) && targetEnd.isAfter(eventStart)) {
        overlappingEvents.push(event)
      }
    })

    return overlappingEvents
  }

  /**
     * Show overlap dialog for priority resolution
     * @param {Object} draggedEvent - The event being moved/resized
     * @param {Array} overlappingEvents - Array of overlapping events
     * @param {Object} info - FullCalendar drop/resize info for potential revert
     */
  showOverlapDialog (draggedEvent, overlappingEvents, info) {
    this.overlapConflict = {
      draggedEvent,
      overlappingEvents,
      info,
      selectedEvent: null,
      newPriority: draggedEvent.extendedProps.priority,
      calculatedPriority: null,
      calculationType: null
    }

    // Store revert info in ModalManager so cancel can revert the drag/resize
    if (info && typeof info.revert === 'function') {
      this.controller.modalManager.pendingChangeInfo = { revert: info.revert }
    }

    this.controller._$log.info('[ScheduleCalendar] Showing overlap dialog for conflict resolution', {
      draggedEventId: draggedEvent.extendedProps.intervalId,
      overlappingCount: overlappingEvents.length,
      overlappingIds: overlappingEvents.map(e => e.extendedProps.intervalId)
    })

    // Show the modal via controller (ModalManagementMixin)
    this.controller.showOverlapModal()
  }

  /**
     * Select which event should win the conflict
     * @param {string} eventType - 'dragged' or 'existing'
     */
  selectConflictWinner (eventType) {
    if (this.overlapConflict) {
      this.overlapConflict.selectedEvent = eventType

      if (eventType === 'dragged') {
        // New interval wins - set higher priority
        const maxConflictingPriority = Math.max(
          ...this.overlapConflict.overlappingEvents.map(e => e.extendedProps.priority)
        )
        this.overlapConflict.calculatedPriority = maxConflictingPriority + 1
        this.overlapConflict.calculationType = 'higher'
      } else {
        // Existing intervals win - set lower priority
        const minConflictingPriority = Math.min(
          ...this.overlapConflict.overlappingEvents.map(e => e.extendedProps.priority)
        )
        this.overlapConflict.calculatedPriority = Math.max(1, minConflictingPriority - 1)
        this.overlapConflict.calculationType = 'lower'
      }
    }
  }

  /**
     * Resolve the overlap conflict with selected priority
     */
  resolveOverlapConflict (conflict) {
    if (conflict) {
      this.overlapConflict = conflict
    }
    if (!this.overlapConflict || !this.overlapConflict.selectedEvent) {
      return
    }

    const draggedEvent = this.overlapConflict.draggedEvent
    const draggedProps = draggedEvent.extendedProps
    let finalPriority = draggedProps.priority

    if (this.overlapConflict.selectedEvent === 'dragged') {
      // New/dragged interval wins
      finalPriority = this.overlapConflict.calculatedPriority

      this.controller._$log.info('[ScheduleCalendar] New/dragged interval wins with priority:', {
        originalPriority: draggedProps.priority,
        calculatedPriority: finalPriority,
        conflictingPriorities: this.overlapConflict.overlappingEvents.map(e => e.extendedProps.priority)
      })
    } else {
      // Existing intervals win
      finalPriority = this.overlapConflict.calculatedPriority

      this.controller._$log.info('[ScheduleCalendar] Existing intervals win, new/dragged gets lower priority:', {
        originalPriority: draggedProps.priority,
        calculatedPriority: finalPriority,
        conflictingPriorities: this.overlapConflict.overlappingEvents.map(e => e.extendedProps.priority)
      })
    }

    // Check if this is a new interval creation (has pendingIntervalData) or an existing interval move
    // pendingIntervalData is currently on the controller (StateManagementMixin)
    if (this.controller.pendingIntervalData) {
      // This is a new interval creation - update the pending data and proceed
      this.controller.pendingIntervalData.priority = finalPriority
      this.controller._$log.info('[ScheduleCalendar] Resolving conflict for new interval creation with priority:', finalPriority)

      this.controller.closeOverlapModal()

      // Proceed with creation using updated priority
      this.controller.proceedWithIntervalCreation(this.controller.pendingIntervalData)
    } else {
      // This is an existing interval move - update the interval
      const newStart = moment(draggedEvent.start)
      const newEnd = moment(draggedEvent.end)

      // updateIntervalTime is currently on the controller (EventHandlersMixin)
      this.controller.updateIntervalTime(draggedProps.intervalId, newStart, newEnd, null, draggedProps.sceneCollectionId, finalPriority)
        .then(() => {
          this.controller._$log.info('[ScheduleCalendar] Conflict resolved successfully for existing interval')
          this.controller.closeOverlapModal()

          // Refresh calendar to update visual priorities
          this.controller._$timeout(() => {
            if (this.controller.calendarManager && this.controller.calendarManager.calendar) {
              this.controller.calendarManager.calendar.refetchEvents()
            }
          }, 0)
        })
        .catch(error => {
          this.controller._$log.error('[ScheduleCalendar] Failed to resolve conflict:', error)
          // Revert the visual change
          if (this.overlapConflict.info && this.overlapConflict.info.revert) {
            this.overlapConflict.info.revert()
          }
          this.controller.closeOverlapModal()
          window.alert('Failed to resolve scheduling conflict. Please try again.')
        })
    }

    // Clear conflict state
    this.overlapConflict = null
  }
}
